// ========== src/components/Dashboards/ParentDashboard.jsx ==========

import { useState, useEffect } from 'react';
import * as api from '../../utils/api';
import { useSubscription } from '../../hooks/useSubscription';
import { UpgradeModal } from '../../components/SubscriptionComponents';
import AddChildModal from '../Modals/AddChildModal';
import ChildCredentialsModal from '../Modals/ChildCredentialsModal';
import ChildSubscriptionModal from '../Modals/ChildSubscriptionModal';

const ParentDashboard = () => {
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childStats, setChildStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentialsChild, setCredentialsChild] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showChildSubModal, setShowChildSubModal] = useState(false);
  const [subModalChild, setSubModalChild] = useState(null);

  const { usage, subscription } = useSubscription();

  const loadChildren = async () => {
    setLoading(true);
    setError(null);
    try {
      const responseData = await api.getChildren();
      if (responseData && Array.isArray(responseData.children)) {
        const childrenList = responseData.children;
        setChildren(childrenList);
        if (childrenList.length > 0) {
          handleChildSelect(childrenList[0]);
        } else {
          setLoading(false);
        }
      } else {
        setError(responseData.detail || 'Nepavyko gauti vaikų sąrašo.');
        setLoading(false);
      }
    } catch (err) {
      console.error("Klaida gaunant vaikus:", err);
      setError('Sistemos klaida gaunant vaikų sąrašą.');
      setLoading(false);
    }
  };

  const loadChildStats = async (childId) => {
    setLoading(true);
    try {
      const statsData = await api.getChildStats(childId);
      if (statsData && !statsData.detail) {
        setChildStats(statsData);
      } else {
        setError(statsData.detail || 'Nepavyko gauti vaiko statistikos.');
        setChildStats(null);
      }
    } catch (err) {
      console.error("Klaida gaunant vaiko statistiką:", err);
      setError('Sistemos klaida gaunant vaiko statistiką.');
    } finally {
      setLoading(false);
    }
  };

  const handleChildSelect = (child) => {
    setSelectedChild(child);
    setChildStats(null);
    loadChildStats(child._id);
  };

  const handleAddChild = () => {
    setShowAddChildModal(true);
  };

  const handleChildAdded = (newChild) => {
    loadChildren();
    setShowAddChildModal(false);
    if (newChild) {
      setCredentialsChild(newChild);
      setShowCredentialsModal(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const handleViewCredentials = (child) => {
    setCredentialsChild(child);
    setShowCredentialsModal(true);
  };

  const handleOpenChildSubModal = (child) => {
    setSubModalChild(child);
    setShowChildSubModal(true);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadChildren();
  }, []);

  if (loading && !selectedChild) {
    return <div className="min-h-screen flex items-center justify-center"><p>Kraunami duomenys...</p></div>;
  }

  if (error) {
    return <div className="min-h-screen flex items-center justify-center text-red-500 text-center p-8">{error}</div>;
  }

  if (children.length === 0) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-pink-50 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 flex justify-between items-center">
              <div>
                <h1 className="text-4xl font-bold text-gray-800 mb-2">👨‍👩‍👧‍👦 Tėvų Paskyra </h1>
                <p className="text-gray-600">Stebėkite savo vaikų pažangą</p>
              </div>
              <button onClick={handleLogout} className="px-6 py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition">Atsijungti</button>
            </div>
            <div className="text-center flex flex-col items-center justify-center mt-20">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Vaikų nerasta</h2>
              <p className="text-gray-600 mb-6">Jūs dar nepridėjote vaikų prie savo paskyros.</p>
              <button onClick={handleAddChild} className="p-4 px-8 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition transform hover:scale-105">Pridėti pirmą vaiką</button>
            </div>
          </div>
        </div>
        <AddChildModal isOpen={showAddChildModal} onClose={() => setShowAddChildModal(false)} onSuccess={handleChildAdded} />
      </>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2">👨‍👩‍👧‍👦 Tėvų Paskyra </h1>
              <p className="text-gray-600">Stebėkite savo vaikų pažangą</p>
            </div>
            <button onClick={handleLogout} className="px-6 py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition transform hover:scale-105">Atsijungti</button>
          </div>

          {/* Subscription Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl shadow-xl p-6 text-white">
              <div className="flex items-center justify-between mb-4">
              </div>
              <h3 className="text-2xl font-bold mb-2">Tėvų Paskyros Planas</h3>
              <p className="text-purple-100 italic mb-2 text-sm">
                Šis planas valdo jūsų paskyros ribas
              </p>
              <p className="text-purple-100 font-bold">
                {subscription?.tier === 'free' && 'Nemokamas planas'}
                {subscription?.tier === 'basic' && 'Bazinis planas'}
                {subscription?.tier === 'premium' && 'Premium planas'}
                {subscription?.tier === 'family' && '👨‍👩‍👧‍👦 Šeimos planas'}
                {subscription?.tier === 'enterprise' && 'Enterprise planas'}
                {!subscription?.tier && 'Nemokamas planas'}
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl shadow-xl p-6 text-white">
              <div className="text-5xl mb-4">🤖</div>
              <h3 className="text-2xl font-bold mb-2">AI Užklausos</h3>
              <p className="text-3xl font-bold">{usage?.daily_remaining || 0}</p>
              <p className="text-blue-100 text-sm">iš {usage?.daily_limit || 0} liko šiandien</p>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-2xl shadow-xl p-6 text-white">
              <div className="text-5xl mb-4">👨‍👩‍👧‍👦</div>
              <h3 className="text-2xl font-bold mb-2">Vaikai</h3>
              <p className="text-3xl font-bold">{children.length}</p>
              <p className="text-green-100 text-sm">
                {subscription?.tier === 'free' && 'Iki 1 vaiko'}
                {subscription?.tier === 'basic' && 'Iki 5 vaikų'}
                {subscription?.tier === 'premium' && 'Neribotai'}
                {subscription?.tier === 'enterprise' && 'Neribotai'}
                {!subscription?.tier && 'Iki 2 vaikų'}
              </p>
            </div>
          </div>

          {/* Upgrade CTA */}
          {(!subscription?.tier || subscription?.tier === 'free') && (
            <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-2xl shadow-2xl p-8 mb-8 text-white">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="text-6xl">🚀</div>
                    <div>
                      <h2 className="text-3xl font-bold mb-1">Atnaujinkite Planą!</h2>
                      <p className="text-yellow-100 text-lg">Gaukite daugiau AI užklausų ir funkcijų</p>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center gap-2">
                      <span className="text-2xl">✓</span>
                      <span>Daugiau AI užklausų kasdien</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-2xl">✓</span>
                      <span>Daugiau vaikų paskyrų</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-2xl">✓</span>
                      <span>Prioritetinė pagalba</span>
                    </li>
                  </ul>
                </div>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="px-8 py-4 bg-white text-orange-600 rounded-2xl font-bold text-xl shadow-lg hover:shadow-xl transition transform hover:scale-105"
                >
                  Atnaujinti Dabar
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Pasirinkite vaiką:</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {children.map((child) => (
                <div key={child._id} className={`p-6 rounded-xl transition transform hover:scale-105 ${selectedChild?._id === child._id ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' : 'bg-gray-100 hover:bg-gray-200'}`}>
                  <div onClick={() => handleChildSelect(child)} className="text-center mb-3 cursor-pointer">
                    <div className="text-6xl mb-2">{child.avatar || '👦'}</div>
                    <h3 className={`text-xl font-bold ${selectedChild?._id === child._id ? 'text-white' : 'text-gray-800'}`}>{child.name}</h3>
                    <p className={`text-sm ${selectedChild?._id === child._id ? 'text-purple-100' : 'text-gray-600'}`}>{child.grade || 'Nenurodyta'} • {child.age || '?'} m.</p>
                    <div className="mt-2 inline-block bg-white bg-opacity-20 px-3 py-1 rounded-full border border-gray-300">
                      <span className={`text-xs font-bold uppercase ${selectedChild?._id === child._id ? 'text-white' : 'text-purple-600'}`}>
                        Planas: {child.plan || 'free'}
                      </span>
                    </div>
                    {child.is_inherited && (
                      <div className="mt-2">
                        <span className="bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-bold border border-green-200">
                          👨‍👩‍👧‍👦 Dengia Šeimos planas
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2 mt-4">
                    <button onClick={(e) => { e.stopPropagation(); handleOpenChildSubModal(child); }} className={`w-full px-3 py-2 rounded-lg text-sm font-bold transition flex justify-center items-center gap-2 ${selectedChild?._id === child._id ? 'bg-purple-700 hover:bg-purple-800 text-white' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}>
                      💳 Pakeisti planą
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleViewCredentials(child); }} className={`w-full px-3 py-2 rounded-lg text-sm font-bold transition flex justify-center items-center gap-2 ${selectedChild?._id === child._id ? 'bg-white text-purple-600 hover:bg-purple-50' : 'bg-blue-500 text-white hover:bg-blue-600'}`}>
                      🔑 Prisijungimai
                    </button>
                  </div>
                </div>
              ))}
              <div onClick={handleAddChild} className="p-6 rounded-xl bg-gradient-to-r from-blue-100 to-purple-100 border-2 border-dashed border-blue-300 cursor-pointer hover:border-blue-400 transition flex items-center justify-center">
                <div className="text-center">
                  <div className="text-5xl mb-2">➕</div>
                  <p className="text-sm font-bold text-gray-700">Pridėti vaiką</p>
                </div>
              </div>
            </div>
          </div>

          {loading && <div className="text-center py-8 font-bold text-gray-500">Kraunama vaiko statistika...</div>}

          {selectedChild && childStats && !loading && (
            <div className="space-y-8">

              {/* ── Gamification juosta ── */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  {
                    icon: '🔥',
                    value: childStats.streak_days ?? 0,
                    label: 'Dienų serija',
                    sub: `Rekordas: ${childStats.longest_streak ?? 0}`,
                    color: '#FF9600',
                    bg: 'linear-gradient(135deg,#FF9600,#FF4B4B)',
                  },
                  {
                    icon: '⭐',
                    value: childStats.total_score ?? 0,
                    label: 'Viso taškų',
                    sub: `Vidu.: ${childStats.average_score ?? 0}`,
                    color: '#FFC800',
                    bg: 'linear-gradient(135deg,#FFC800,#FF9600)',
                  },
                  {
                    icon: '🎮',
                    value: childStats.total_games ?? 0,
                    label: 'Žaidimų',
                    sub: `Baigta: ${childStats.completed_games ?? 0}`,
                    color: '#1CB0F6',
                    bg: 'linear-gradient(135deg,#1CB0F6,#667eea)',
                  },
                  {
                    icon: '⚡',
                    value: `L${childStats.level ?? 1}`,
                    label: 'Lygis',
                    sub: `XP: ${childStats.xp ?? 0}`,
                    color: '#CE82FF',
                    bg: 'linear-gradient(135deg,#CE82FF,#764ba2)',
                  },
                ].map(s => (
                  <div
                    key={s.label}
                    className="rounded-3xl p-5 text-center text-white shadow-lg"
                    style={{ background: s.bg }}
                  >
                    <div className="text-3xl mb-1">{s.icon}</div>
                    <p className="text-3xl font-black">{s.value}</p>
                    <p className="text-xs font-black uppercase opacity-80 mt-0.5">{s.label}</p>
                    <p className="text-xs opacity-60 mt-0.5">{s.sub}</p>
                  </div>
                ))}
              </div>

              {/* ── Pasiekimai ── */}
              {childStats.recent_achievements && childStats.recent_achievements.length > 0 && (
                <div className="bg-white rounded-3xl shadow-lg p-6">
                  <h3 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
                    🏅 Paskutiniai pasiekimai
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {childStats.recent_achievements.map((a, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 px-4 py-2 rounded-2xl border-2 font-bold text-sm"
                        style={{ background: '#FFFBEA', borderColor: '#FFC800', color: '#B8860B' }}
                      >
                        <span className="text-xl">{a.icon}</span>
                        <span>{a.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Weekly Activity Chart */}
                <div className="bg-white p-6 rounded-3xl shadow-xl">
                  <h3 className="text-xl font-black text-gray-800 mb-6">📈 Savaitės aktyvumas</h3>
                  <div className="flex items-end justify-between h-40 gap-2">
                    {childStats.weekly_activity && childStats.weekly_activity.map((day, index) => (
                      <div key={index} className="flex flex-col items-center flex-1">
                        <div
                          className="w-full rounded-t-xl transition-all duration-500"
                          style={{
                            height: `${Math.max((day.count / Math.max(...childStats.weekly_activity.map(d => d.count), 1)) * 100, 8)}%`,
                            background: day.count > 0
                              ? 'linear-gradient(180deg, #58CC02, #46A302)'
                              : '#E5E5E5',
                            minHeight: 8,
                          }}
                        />
                        <p className="text-xs text-gray-500 mt-2 font-bold">{day.day_name}</p>
                        <p className="text-xs text-gray-400">{day.count}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subject Performance */}
                <div className="bg-white p-6 rounded-2xl shadow-xl">
                  <h3 className="text-xl font-bold text-gray-800 mb-6">Pasiekimai pagal temas</h3>
                  <div className="space-y-4">
                    {childStats.subject_performance && childStats.subject_performance.length > 0 ? (
                      childStats.subject_performance.map((subject, index) => (
                        <div key={index}>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm font-bold text-gray-700 capitalize">{subject.subject}</span>
                            <span className="text-sm text-gray-500">{subject.average_score} tšk.</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-blue-400 to-purple-500 h-3 rounded-full"
                              style={{ width: `${Math.min(subject.average_score, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-center py-8">Nėra duomenų apie temas.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Games */}
              <div className="bg-white p-6 rounded-2xl shadow-xl">
                <h3 className="text-xl font-bold text-gray-800 mb-6">Paskutiniai žaidimai</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left border-b border-gray-100">
                        <th className="pb-3 text-gray-500 font-bold text-sm">Data</th>
                        <th className="pb-3 text-gray-500 font-bold text-sm">Žaidimas</th>
                        <th className="pb-3 text-gray-500 font-bold text-sm">Rezultatas</th>
                        <th className="pb-3 text-gray-500 font-bold text-sm">Būsena</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {childStats.recent_games && childStats.recent_games.length > 0 ? (
                        childStats.recent_games.map((game) => (
                          <tr key={game.id} className="hover:bg-gray-50 transition">
                            <td className="py-3 text-gray-600 text-sm">
                              {new Date(game.date).toLocaleDateString('lt-LT', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="py-3 font-bold text-gray-800 capitalize">{game.game_type}</td>
                            <td className="py-3 text-purple-600 font-bold">{game.score}</td>
                            <td className="py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${game.completed ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                                {game.completed ? 'Baigta' : 'Nebaigta'}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="py-8 text-center text-gray-500">Žaidimų istorija tuščia.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <AddChildModal isOpen={showAddChildModal} onClose={() => setShowAddChildModal(false)} onSuccess={handleChildAdded} />
      <ChildCredentialsModal isOpen={showCredentialsModal} onClose={() => setShowCredentialsModal(false)} child={credentialsChild} />
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
      <ChildSubscriptionModal isOpen={showChildSubModal} onClose={() => setShowChildSubModal(false)} child={subModalChild} />
    </>
  );
};

export default ParentDashboard;